import { Injectable } from '@angular/core';
import { SettingsProvider } from '../settings';
import { ModalProvider } from './modal';
import { NotificationResult, ModalProperties } from './base';
import { TranslateService } from '@ngx-translate/core';

/**
 * String names for existing modals (for storage key generation)
 * Notifications only get listed here if they can be muted (aka "Don't show me this again")
 */
export enum Muteable {
    /* Deletion Warnings */
    DeleteAsset = 'warning.deleteAsset',
    DeleteClass = 'warning.deleteClass',
    DeleteDataset = 'warning.deleteDataset',
    DeleteExportDetails = 'warning.deleteExportDetails',
    DeleteProject = 'warning.deleteProject',
    RevokeKey = 'warning.revokeKey',

    /* Polygon Warnings */
    TagMultiplePolygons = 'warning.tagMultiplePolygons',
    TagMultipleAssets = 'warning.tagMultipleAssets',
    OverlappingPolygons = 'warning.overlappingPolygons',
    BulkDeletePolygons = 'warning.bulkDeletePolygons',
    LeaveWithoutSaving = 'warning.unsavedChangesDetected',
    ClearPolygons = 'warning.clearMultiplePolygons',
    ClearPoints = 'warning.clearPoints',
    RevertWorkingChanges = 'warning.revertWorkingChanges',

    /* Information Modals */
    AnomalyWorkflowModal = 'AnomalyWorkflowModal'
}

/**
 * Use this modal when presenting it requires user authentication
 * Currently this means integration with the doNotShow/mute functionality, which requires the settingsProvider
 */
@Injectable()
export class MuteableModalProvider {
    constructor(
        private modalProvider: ModalProvider,
        private settingsProvider: SettingsProvider,
        private translate: TranslateService
    ) {}

    public async isMuted(name: Muteable): Promise<boolean> {
        await this.settingsProvider.load();
        return this.settingsProvider.get(this.getMuteKey(name)) == true;
    }

    public async setMutedState(name: Muteable, state: boolean) {
        await this.settingsProvider.set(this.getMuteKey(name), state);
    }

    public getMuteKey(name: Muteable): string {
        return `notifications.${name}.doNotShow`;
    }

    public async presentConfirmationPrompt(
        muteable: Muteable,
        title: string,
        message: string,
        primaryText = 'Okay',
        customCSS?: string,
        force = false
    ): Promise<boolean> {
        const result = await this.createNotification(
            muteable,
            {
                title,
                message,
                primaryText,
                customCSS,
                secondaryButtons: [{ text: this.translate.instant('Cancel'), callback: () => {} }]
            },
            force
        );

        return result !== undefined && result !== null && result.primaryClicked == true;
    }

    public async presentAlert(
        muteable: Muteable,
        title: string,
        message: string,
        primaryText = 'Okay',
        customCSS?: string,
        force = false
    ): Promise<void> {
        await this.createNotification(
            muteable,
            {
                title,
                message,
                primaryText,
                customCSS
            },
            force
        );
    }

    public async createNotification(
        muteable: Muteable,
        properties: ModalProperties,
        force = false,
        allowDoNotShow = true
    ): Promise<NotificationResult> {
        if (!force && (await this.isMuted(muteable))) {
            return { primaryClicked: true };
        }

        const result = await new Promise<NotificationResult | undefined>(async resolve => {
            const modal = this.modalProvider.createModal(
                'NotificationModal',
                { ...properties, allowDoNotShow },
                { cssClass: `notification-modal ${properties.customCSS}` }
            );

            modal.onDidDismiss(data => resolve(data));
            modal.present();
        });

        if (result && result.doNotShowAgain) {
            await this.setMutedState(muteable, true);
        }

        return result || { primaryClicked: false };
    }

    /**
     * Confirmation Prompts
     */

    public async presentDeleteAssetPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.DeleteAsset,
            this.translate.instant('Confirm Batch Delete'),
            this.translate.instant('Are you sure that you would like to delete the selected assets?'),
            this.translate.instant('Delete Assets')
        );
    }

    public async presentDeleteClassPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.DeleteClass,
            this.translate.instant('Delete Class'),
            this.translate.instant(
                'Are you sure you want to delete this class? This will remove it and delete all associated annotations as well.'
            ),
            this.translate.instant('Delete')
        );
    }

    public async presentDeleteDatasetPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.DeleteDataset,
            this.translate.instant('Delete Dataset'),
            this.translate.instant(
                'Are you sure you want to delete this dataset? This will remove it and delete any assets you have uploaded as well.'
            ),
            this.translate.instant('Delete')
        );
    }

    public async presentDeleteProjectPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.DeleteProject,
            this.translate.instant('Delete Project'),
            this.translate.instant(
                'Are you sure you want to delete this project? This will remove it and delete any images you have uploaded as well.'
            ),
            this.translate.instant('Delete')
        );
    }

    public async presentDeleteBulkPolygonsPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.BulkDeletePolygons,
            this.translate.instant('Bulk Delete Polygons'),
            this.translate.instant(
                'Are you sure you want to delete all associated polygons? This action cannot be undone.'
            ),
            this.translate.instant('Delete')
        );
    }

    public async presentTagMultiplePolygonsPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.TagMultiplePolygons,
            this.translate.instant('Batch Selection'),
            this.translate.instant(
                'You have selected multiple polygons to be tagged. Are you sure you want to tag them all?'
            ),
            this.translate.instant('Tag All Selected')
        );
    }

    public async presentClearPointsPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.ClearPoints,
            this.translate.instant('Invalid Polygon'),
            this.translate.instant(
                'Polygons cannot exist without three (3) or more points. Proceeding with this action will delete the polygon this point belonged to. Are you sure you want to do this?'
            ),
            this.translate.instant('Delete')
        );
    }

    public async presentClearPolygonsPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.ClearPolygons,
            this.translate.instant('Clear Polygons'),
            this.translate.instant('Are you sure? All polygons will be removed from the current asset.'),
            this.translate.instant('Delete')
        );
    }

    public async presentRevertWorkingChangesPrompt(): Promise<boolean> {
        return await this.presentConfirmationPrompt(
            Muteable.RevertWorkingChanges,
            this.translate.instant('Revert All Changes'),
            this.translate.instant(
                'Reverting to the previously saved state will discard changes from this session. Would you like to continue?'
            ),
            this.translate.instant('Revert Changes')
        );
    }
}



// WEBPACK FOOTER //
// ./src/providers/popup/muteable-modal.ts